import {EnvironmentTypes} from "./environment.types";

/**
 * Derives which DEPLOYMENT the SPA is currently running on, from the browser's hostname.
 *
 * ── Why this is a runtime derivation and not a build-time constant ──────────────────────────────────
 *
 * There is exactly ONE production bundle. `angular.json` declares a single `production` configuration
 * whose `fileReplacements` swap `environment.ts` for `environment.prod.ts`, Pipeline A builds that
 * artifact once, config-free, and Pipeline B ships the very same image to int01, prev01 and prod01. No
 * `environment.*.ts` constant, no `define`, no `--configuration` can tell those three apart — which is
 * exactly how Bug #443 happened: `initSentry()`'s `environment.production ? Production : Development`
 * ternary runs only inside `if (environment.production)`, so it could only ever yield `"production"`,
 * and every browser error from all three deployments landed in the same Sentry bucket.
 *
 * The .NET hosts do not have this problem — Pipeline B bakes a per-environment
 * `appsettings.Production.json` overlay carrying `SENTRY_ENVIRONMENT`. The SPA has no such overlay, so
 * the only signal available at runtime is `location.hostname`. That is the same signal the SPA already
 * trusts for `BASE_URL` (`https://api.${location.hostname}`, see `endpoint.config.ts`), so this adds no
 * new class of assumption.
 *
 * ── Why this is a RULE with a default, and not a lookup table ───────────────────────────────────────
 *
 * The hostname set is not enumerable. prod01 alone serves seven names — the canonical
 * `prod01.cheqme.app` plus six live venue aliases (`alex-beach`, `dwc`, `heidenauer-bemme`, `rush`,
 * `stolperdiele`, `ws`), and nginx answers for all of them via the regex `server_name
 * ~^api\.[\w|-]*\.cheqme\.app$` (see `docs/articles/platform/prod01-onboarding-and-cutover-runbook.md`).
 * Onboarding a new venue is a DNS + alias change with no repo change at all, so any list of production
 * hostnames maintained here would be stale the day a customer goes live.
 *
 * So: the small, stable, non-customer-facing environments are named explicitly, and EVERYTHING ELSE
 * defaults to `production`.
 *
 * ── Why `production` is the correct default, and the choice is asymmetric ───────────────────────────
 *
 * The two failure directions are not equally bad:
 *
 *   * A production host mislabelled `internal`/`preview` DISAPPEARS from every production alert, saved
 *     search and release-health view. A real customer-facing outage becomes invisible — the exact class
 *     of silent failure behind Bug #440.
 *   * An internal host mislabelled `production` merely adds noise to a view an operator is already
 *     reading, and is self-correcting: somebody notices the wrong hostname on the event and adds the
 *     label below.
 *
 * A newly onboarded venue is by definition an unknown hostname, so the default is also the *likely*
 * answer, not just the safe one. Hence: unknown ⇒ `production`.
 */

/**
 * Hostnames that are the developer's own machine rather than any deployment. `*.localhost` is included
 * because it resolves to loopback per RFC 6761 and some dev proxies use it.
 *
 * This branch is reachable in the PRODUCTION bundle: `endpoint.config.ts` already special-cases
 * `localhost` under `environment.production`, i.e. running the built web image locally is a supported
 * thing to do, and such a run must not file events as if it came from a customer's till.
 */
const LOCAL_HOSTNAMES: ReadonlySet<string> = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "::1",
  "[::1]"
]);

/** Every deployment is served under this apex; anything else is somebody's own domain. */
const DEPLOYMENT_DOMAIN = "cheqme.app";

/**
 * Subdomain labels int01 answers to.
 *
 * `dev1` is NOT a typo and must not be "cleaned up": int01 is a repurposed `dev1` box that kept its old
 * name, both names are registered as Entra reply URLs precisely so a rename needs no directory change,
 * and today `dev1.cheqme.app` is the one that actually resolves (`infra/scripts/render-dotenv.sh`,
 * `infra/scripts/arc-onboard.sh`). Dropping it would tag every real int01 event `production`.
 */
const INTERNAL_LABELS: ReadonlySet<string> = new Set(["int", "dev1"]);

/**
 * Subdomain labels prev01 answers to.
 *
 * `prev` and `prev01` are the two hostnames registered for it during the tenant-B migration
 * (`docs/articles/platform/tenant-b-migration-runbook.md` § 5: `api.prev.cheqme.app` +
 * `api.prev01.cheqme.app`).
 *
 * `dge` and `blue` are the prev aliases named in `infra/scripts/create-app-registrations.sh`
 * ("prev answers to prev01 / dge / blue.cheqme.app"), confirmed by the operator on 2026-08-18 as
 * PERMANENTLY prev01-only.
 *
 * ── That confirmation is load-bearing, and it is the one dependency this rule takes on ──────────────
 *
 * `dge` and `blue` are TENANT aliases, not infrastructure names: a company alias becomes a subdomain
 * (see `Common.Extensions/Extensions/StringExtensions.TryGetAliasOrFallback`), and a tenant alias is
 * repointed by a DNS + alias change that touches no code. So naming them here makes this file depend on
 * a fact it cannot itself verify. If either is ever repointed at prod01, its events would be filed as
 * `preview` and vanish from every production alert and saved search — precisely the failure the
 * `production` default below exists to prevent.
 *
 * That trade is accepted DELIBERATELY, not by oversight. The operator holds the fact this file cannot:
 * these two are fixed. Correctly attributing prev01's real traffic is worth a risk gated on a decision
 * only the operator makes — and if that decision is ever revisited, this list must be revisited in the
 * same change.
 *
 * The bar for anything else is unchanged: add a label here only for a host that is structurally never
 * customer-facing, or one an operator has explicitly confirmed as pinned to a non-production
 * deployment. Never on inference from a hostname's shape.
 */
const PREVIEW_LABELS: ReadonlySet<string> = new Set(["prev", "prev01", "dge", "blue"]);

/**
 * Maps a browser hostname to the deployment it identifies.
 *
 * Pure and dependency-free on purpose: it takes the hostname as an argument rather than reading
 * `window.location` itself, so it unit-tests directly with plain Jasmine and needs neither TestBed nor
 * a stubbed global.
 *
 * @param hostname `location.hostname` — no scheme, no port, no path.
 * @returns the matching {@link EnvironmentTypes}; {@link EnvironmentTypes.Production} for anything
 *          unrecognised, which is the deliberate safe default (see the file comment).
 */
export function resolveEnvironmentType(hostname: string | null | undefined): EnvironmentTypes {
  // Normalise defensively: hostnames are case-insensitive, and a fully-qualified name may carry a
  // trailing root dot. A missing/blank hostname is not a reason to under-report, so it falls through
  // to the default like any other unrecognised value.
  const host = (hostname ?? "").trim().toLowerCase().replace(/\.$/, "");

  if (LOCAL_HOSTNAMES.has(host) || host.endsWith(".localhost")) {
    return EnvironmentTypes.Development;
  }

  // Not under the deployment apex ⇒ nothing here can classify it. Default.
  if (!host.endsWith(`.${DEPLOYMENT_DOMAIN}`)) {
    return EnvironmentTypes.Production;
  }

  // The deployment label is the one immediately left of `cheqme.app`. Taking the LAST label of the
  // remainder (rather than the first) means a service-prefixed name such as `api.prev.cheqme.app`
  // classifies as `prev` too — the SPA itself is only ever served on the apex vhost, but a resolver
  // that answers differently for the API host of the same deployment would be a trap for a caller.
  const labels = host.slice(0, -(DEPLOYMENT_DOMAIN.length + 1)).split(".");
  const label = labels[labels.length - 1];

  if (INTERNAL_LABELS.has(label)) return EnvironmentTypes.Internal;
  if (PREVIEW_LABELS.has(label)) return EnvironmentTypes.Preview;

  return EnvironmentTypes.Production;
}
